import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Prompt } from 'react-router';
import { Location } from 'history';
import { useNavigate, useLocation } from 'react-router-dom-v5-compat';
import {
  MODAL_NAMES,
  ProjectSettingsFormSchemaType,
  useProjectSettingsModalsContext,
} from 'modules/projectSettings/contexts';
import { FIELD_NAMES } from 'modules/projectSettings/constants';
import { UnsavedChangesModal } from 'modules/projectSettings/components/modals/UnsavedChangesModal';
import { useUnsavedChangesPrompt } from 'modules/ai/ui/components/UnsavedChanges/useUnsavedChangesModal';
import { useSubmitProjectSettings } from 'modules/projectSettings/hooks/useSubmitProjectSettings';
import { AddLocaleModal } from './AddLocaleModal';
import { AddNewLabelModal } from './AddNewLabelModal';
import { ConfirmDisableAnonUserTrackingModal } from './ConfirmDisableAnonUserTrackingModal';
import { DeleteLabelModal } from './DeleteLabelModal';
import { DeleteLocaleModal } from './DeleteLocaleModal';
import { EditLabelModal } from './EditLabelModal';
import { SetDefaultLocaleModal } from './SetDefaultLocaleModal';
import { WALocaleModal } from './WALocaleModal';

const { LOCALES, MAPPED_LOCALES } = FIELD_NAMES;

export const ProjectSettingsModals = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { modalState, showModal } = useProjectSettingsModalsContext();
  const { isDirty } = useSubmitProjectSettings();
  const { getValues, setValue } = useFormContext<ProjectSettingsFormSchemaType>();

  const { changesConfirmed, confirmChanges, nextLocation, promptForUnsavedChanges } =
    useUnsavedChangesPrompt(() => {
      showModal({ name: MODAL_NAMES.UNSAVED_CHANGES });
    }, MODAL_NAMES.UNSAVED_CHANGES);

  const compareWithPathnames = (nextLoc: Location) => {
    if (pathname === nextLoc.pathname) {
      return true;
    } else {
      return promptForUnsavedChanges(nextLoc);
    }
  };

  const { name } = modalState || {};

  // Values
  const locales = getValues(LOCALES);
  const mappedLocales = getValues(MAPPED_LOCALES);

  useEffect(() => {
    if (changesConfirmed && nextLocation) {
      navigate(nextLocation?.pathname || '');
    }
  }, [changesConfirmed, nextLocation, navigate]);

  let modalContent = null;
  switch (name) {
    case MODAL_NAMES.UNSAVED_CHANGES:
      modalContent = <UnsavedChangesModal onConfirm={confirmChanges} />;
      break;

    /** Locales */
    case MODAL_NAMES.ADD_LOCALE:
      modalContent = <AddLocaleModal locales={locales} setValue={setValue} />;
      break;
    case MODAL_NAMES.WA_LOCALE:
      modalContent = (
        <WALocaleModal locales={locales} mappedLocales={mappedLocales} setValue={setValue} />
      );
      break;
    case MODAL_NAMES.DELETE_LOCALE:
      modalContent = <DeleteLocaleModal />;
      break;

    case MODAL_NAMES.SET_DEFAULT_LOCALE:
      modalContent = <SetDefaultLocaleModal />;
      break;

    /** User Tracking */
    case MODAL_NAMES.CONFIRM_DISABLE_ANON_USER_TRACKING:
      modalContent = <ConfirmDisableAnonUserTrackingModal />;
      break;

    /** Labels */
    case MODAL_NAMES.ADD_NEW_LABEL:
      modalContent = <AddNewLabelModal />;
      break;
    case MODAL_NAMES.DELETE_LABEL:
      modalContent = <DeleteLabelModal />;
      break;
    case MODAL_NAMES.EDIT_LABEL:
      modalContent = <EditLabelModal />;
      break;
    default:
      break;
  }

  return (
    <>
      <Prompt when={isDirty} message={compareWithPathnames} />
      {modalContent}
    </>
  );
};
